import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import SovereigntyBanner from '@/components/SovereigntyBanner';
import PasswordCreator from '@/components/PasswordCreator';
import { BRAND } from '@/lib/constants';
import { AUTH } from '@/constants/testIds';

const ROLE_OPTIONS = [
  { id: 'investor', label: 'I am an Investor — browse startups' },
  { id: 'startup_member', label: 'I am a Startup Member / Operator' },
  { id: 'guest', label: 'Just exploring' },
];

export default function Signup() {
  const [params] = useSearchParams();
  const initialRole = params.get('role') || 'investor';
  // Plan carried over from /pricing — surfaced as a "Plan preview" card
  // and forwarded to /dashboard after a successful signup so the user
  // never has to re-enter it.
  const carriedPersona = params.get('persona') || '';
  const carriedBilling = params.get('billing') || '';
  const carriedModules = (params.get('modules') || '').split(',').filter(Boolean);
  const hasCarriedPlan = carriedPersona && carriedModules.length > 0;
  const carriedParamString = params.toString();

  const [form, setForm] = useState({
    name: '', email: '', password: '', role: initialRole, company: '',
    remember_me: true,
  });
  // v266.2 — confirm-password field lives outside the main form state
  // because it's UI-only; the shared PasswordCreator owns visibility +
  // rule checks.
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const { signup, user, loading: authLoading } = useAuth();
  const nav = useNavigate();

  // v270.3 — If the user is already authenticated (valid session cookie)
  // and lands on /signup, redirect to /dashboard rather than show the
  // signup form. Mirrors the same fix applied to /login.
  useEffect(() => {
    if (!authLoading && user) {
      nav('/dashboard', { replace: true });
    }
  }, [authLoading, user, nav]);

  // Founders may only register through the dedicated /founder-apply flow
  // which collects a mandatory pitch deck + summary. If a user lands on
  // /signup?role=founder we redirect them so they cannot create a bare
  // founder account without a pitch.
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on initial-redirect effects */
  useEffect(() => {
    if (initialRole === 'founder') { nav('/founder-apply', { replace: true }); }
  }, []);

  useEffect(() => {
    const r = params.get('role');
    if (r && ROLE_OPTIONS.some((o) => o.id === r)) {
      setForm((f) => ({ ...f, role: r }));
    }
    if (r === 'founder') {
      nav('/founder-apply', { replace: true });
    }
  }, [params, nav]);
  /* eslint-enable */

  const submit = async (e) => {
    e.preventDefault();
    // PasswordCreator gates isPasswordValid; keep a defensive check
    // so the server-side error message surfaces cleanly if a user
    // clicks submit via keyboard on a subtly-invalid state.
    if (!isPasswordValid) {
      toast.error('Please satisfy every password rule before submitting.');
      return;
    }
    setBusy(true);
    try {
      await signup(form);
      if (hasCarriedPlan) {
        // User already picked their plan before signing up — go straight
        // to the dashboard with the selection preserved as query params
        // so the subscribe-flow surface can finish provisioning.
        toast.success('Welcome — finishing your setup.');
        nav(`/dashboard?subscribed=1&${carriedParamString}`);
      } else {
        toast.success('Welcome to Vigilant Entities — pick your modules.');
        nav('/pricing?welcome=1');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create account');
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block ve-dark-section ve-grain overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={BRAND.abstractBg} alt="" className="h-full w-full object-cover opacity-50"/>
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/85 to-[#000000]/70"/>
        </div>
        <div className="h-full flex flex-col p-12 text-white">
          <Logo dark/>
          <div className="mt-auto">
            <div className="ve-eyebrow text-white/60">Build · Vet · Partner</div>
            <h2 className="display text-4xl xl:text-5xl mt-4 leading-tight">
              Where the world&apos;s best founders & investors meet — compliantly.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-md">
              AI pitch vetting · founder psychometrics · NDA + MOU automation · anonymous chat — all in one platform.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-[#F7F5F4] dark:bg-[#0A0A0A] text-[#0A0A0A] dark:text-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo/></div>
          <div className="ve-eyebrow text-[var(--ve-violet-bright)]">
            {hasCarriedPlan ? 'Step 2 of 2 · Create your account' : 'Create your account'}
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2">
            {hasCarriedPlan ? 'Almost there.' : 'Get started.'}
          </h1>

          {hasCarriedPlan && (
            <div
              className="mt-6 p-4 rounded-lg border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)]"
              data-testid="signup-plan-preview"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--ve-violet-bright)]">
                  Your selected plan
                </span>
                <Link
                  to={`/pricing?${carriedParamString}`}
                  className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--ve-violet-bright)] hover:underline"
                  data-testid="signup-edit-plan"
                >
                  Edit ↗
                </Link>
              </div>
              <div className="text-sm font-semibold capitalize">
                {carriedPersona} build · {carriedBilling || 'monthly'}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                {carriedModules.length} module{carriedModules.length === 1 ? '' : 's'} selected
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Full Name
              <input required value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} data-testid={AUTH.signupName}
                placeholder="Your name"
                className="mt-1.5 w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] normal-case tracking-normal"/>
            </label>
            <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Work Email
              <input required type="email" value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} data-testid={AUTH.signupEmail}
                placeholder="you@company.com"
                className="mt-1.5 w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] normal-case tracking-normal"/>
            </label>
            <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Company / Organisation
              <input value={form.company} onChange={(e) => setForm({...form, company:e.target.value})} data-testid="signup-company"
                placeholder="Optional"
                className="mt-1.5 w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] normal-case tracking-normal"/>
            </label>

            {/* v266.4 — shared PasswordCreator: live rules, strength meter,
                confirm field, dead-disabled submit until every rule is
                green. Pass email + name so the NIST 800-63B "no name /
                email in password" rules light up too. */}
            <PasswordCreator
              value={form.password}
              onValueChange={(p) => setForm((f) => ({ ...f, password: p }))}
              confirmValue={confirmPassword}
              onConfirmChange={setConfirmPassword}
              onValidChange={setIsPasswordValid}
              email={form.email}
              name={form.name}
              variant="light"
              idPrefix="signup-pwd"
              passwordLabel="Password"
              confirmLabel="Confirm password"
              placeholder="12+ chars · mix upper/lower/digit/symbol"
              disabled={busy}
            />
            <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">I am a
              <select value={form.role} onChange={(e) => setForm({...form, role:e.target.value})} data-testid={AUTH.signupRole}
                className="mt-1.5 w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm text-[#0A0A0A] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] normal-case tracking-normal">
                {ROLE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <label className="flex items-start gap-2 cursor-pointer select-none rounded-md border border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.025] p-3 hover:bg-slate-50 dark:hover:bg-white/[0.04]" data-testid="signup-remember-me-label">
              <input type="checkbox" checked={form.remember_me}
                onChange={(e) => setForm({...form, remember_me: e.target.checked})}
                data-testid="signup-remember-me-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-400 text-[#8B3FC5] focus:ring-[#8B3FC5]/30 cursor-pointer"/>
              <div className="text-[12px] leading-snug normal-case tracking-normal">
                <div className="font-semibold text-slate-800 dark:text-white/90">Remember me for 30 days</div>
                <div className="text-slate-500 dark:text-white/55 mt-0.5">Stay signed in on this browser after signup. Revoke any time from Profile &rarr; Security.</div>
              </div>
            </label>
            <button type="submit" disabled={busy || !isPasswordValid} data-testid={AUTH.signupSubmit} className="w-full ve-btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed">
              {busy
                ? 'Creating account…'
                : !isPasswordValid
                  ? 'Fill in a valid password'
                  : (hasCarriedPlan ? 'Create account & start' : 'Create account')} <ArrowRight size={14}/>
            </button>
          </form>

          <p className="mt-8 text-sm text-slate-600 dark:text-slate-300">
            Already have an account? <Link to="/login" className="text-[#8B3FC5] font-medium hover:underline">Sign in</Link>
          </p>

          <SovereigntyBanner size="sm" className="mt-6 w-full" />
        </div>
      </div>
    </main>
  );
}
